import { customFetch } from "@/client/custom-fetch";

import { TPlan, TPlanForBackend } from "../clients/plans.types";
import { TPlanResponse } from "../clients/use-client-plans.hooks";
import {
  TCreateProjectRequest,
  TCreateProjectResponse,
  TProjectDetail,
  TProjectListItem,
  TUpdateProjectRequest,
} from "./projects.types";

// GET - Listar todos os planos da organização
export const getPlans = async (organizationId: string): Promise<TPlan[]> => {
  const result = await customFetch<TPlan[]>(
    `/organizations/${organizationId}/plans`,
    {
      method: "GET",
    }
  );
  return result;
};

// DELETE - Deletar um projeto
// Rota: /projects/:id
// O organizationId vai no header x-organization-id (adicionado automaticamente pelo customFetch)
export const deleteProject = async (
  projectId: string
): Promise<{ success: boolean }> => {
  return customFetch<{ success: boolean }>(`/projects/${projectId}`, {
    method: "DELETE",
  });
};

// PATCH - Atualizar projeto
// Rota: /projects/:id
// O organizationId vai no header x-organization-id (adicionado automaticamente pelo customFetch)
export const updateProject = async (
  projectId: string,
  data: TUpdateProjectRequest
): Promise<{ success: boolean; message: string }> => {
  return customFetch<{ success: boolean; message: string }>(
    `/projects/${projectId}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );
};

/** Append-only: associa sub-parceiros ao projeto sem substituir a lista. */
export const associateOrganizationParticipants = async (
  projectId: string,
  organizationIds: string[],
): Promise<{
  success: boolean;
  message: string;
  added: string[];
  already_linked: string[];
}> => {
  return customFetch(`/projects/${projectId}/organization-participants`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ organization_ids: organizationIds }),
  });
};

// POST - Criar projeto
export const createProject = async (
  data: TCreateProjectRequest,
  organizationId: string
): Promise<TCreateProjectResponse> => {
  return customFetch<TCreateProjectResponse>("/projects", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-organization-id": organizationId,
    },
    body: JSON.stringify(data),
  });
};

// GET - Listar todos os projetos de uma organização
// A rota é /projects/organizations/{id} e o header x-organization-id é adicionado automaticamente pelo customFetch
export const getProjectsByOrganization = async (
  organizationId: string
): Promise<TProjectListItem[]> => {
  const result = await customFetch<TProjectListItem[] | { data: TProjectListItem[] }>(
    `/projects/organizations/${organizationId}`,
    {
      method: "GET",
    }
  );
  
  // Se a resposta vier dentro de um objeto 'data', extrair
  if (result && typeof result === 'object' && 'data' in result && Array.isArray((result as any).data)) {
    return (result as any).data;
  }
  
  // Caso contrário, retornar diretamente
  return Array.isArray(result) ? result : [];
};

// GET - Obter detalhes de um projeto específico
// A rota é /projects/{id} e o header x-organization-id é adicionado automaticamente pelo customFetch
// Retorna os detalhes do projeto incluindo organization_participants (da tabela projects_has_organization_participant)
export const getProjectById = async (
  projectId: string
): Promise<TProjectDetail> => {
  return customFetch<TProjectDetail>(
    `/projects/${projectId}`,
    {
      method: "GET",
    }
  );
};

// GET - Listar todos os planos do usuário (da organização)
// DEPRECATED: Use getProjectPlans para buscar planos de um projeto específico
export const getUserPlans = async (organizationId: string): Promise<TPlanResponse[]> => {
  const result = await customFetch<TPlan[]>(
    `/organizations/${organizationId}/plans`,
    {
      method: "GET",
    }
  );
  // A API retorna TPlan[] mas precisamos de TPlanResponse[] que tem o campo id
  // Fazendo cast similar ao usado em outros lugares do código
  return result as unknown as TPlanResponse[];
};

// GET - Listar planos de um projeto específico
// Usa getProjectById para obter os detalhes do projeto, que inclui os planos associados
// O header x-organization-id é adicionado automaticamente pelo customFetch
export const getProjectPlans = async (projectId: string): Promise<TPlanResponse[]> => {
  const projectDetail = await getProjectById(projectId);
  // Os planos vêm no campo plans do TProjectDetail
  if (!projectDetail.plans) {
    return [];
  }
  // Converter para TPlanResponse[] (que tem o campo id)
  return projectDetail.plans.map((plan) => ({
    id: plan.id,
    name: plan.name,
    description: plan.description,
    plan_price: plan.plan_price,
    organization_id: "",
    is_active: true,
    commission_lifetime_other_installments: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    products: [],
    commission_distribution: [],
  })) as unknown as TPlanResponse[];
};

// POST - Criar planos e associar automaticamente a um projeto
// Usa a rota antiga /organizations/{organizationId}/plans para criar o plano
// Depois associa automaticamente ao projeto na tabela project_has_plans
// Retorna os IDs dos planos criados (se a API retornar) ou busca após criação
export const createPlanAndAssociateToProject = async (
  organizationId: string,
  projectId: string,
  data: TPlanForBackend[]
): Promise<string[]> => {
  // Primeiro, criar os planos na rota antiga
  // Tentar obter resposta com IDs dos planos criados
  const createResponse = await customFetch<any>(`/organizations/${organizationId}/plans`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  let planIds: string[] = [];

  // Se a API retornar os IDs dos planos criados diretamente
  if (createResponse && Array.isArray(createResponse)) {
    planIds = createResponse.map((plan: any) => plan.id || plan.plan_id).filter(Boolean);
  } else if (createResponse && createResponse.plan_ids) {
    planIds = createResponse.plan_ids;
  } else if (createResponse && createResponse.data && Array.isArray(createResponse.data)) {
    planIds = createResponse.data.map((plan: any) => plan.id || plan.plan_id).filter(Boolean);
  } else {
    // Se não retornar IDs, buscar os planos após criação e filtrar pelos nomes
    const plans = await customFetch<TPlan[]>(`/organizations/${organizationId}/plans`, {
      method: "GET",
    });
    
    const planNames = data.map(plan => plan.name);
    const createdPlans = plans.filter(plan => planNames.includes(plan.name));
    planIds = createdPlans.map(plan => (plan as any).id || (plan as any).plan_id).filter(Boolean);
  }

  // Associar os planos ao projeto na tabela project_has_plans
  if (planIds.length > 0) {
    await associatePlansToProject(projectId, planIds);
  }

  return planIds;
};

// POST - Associar planos a um projeto
// A rota é /projects/plans e o header x-organization-id é adicionado automaticamente pelo customFetch
export const associatePlansToProject = async (
  projectId: string,
  planIds: string[]
): Promise<void> => {
  await customFetch<void>(
    `/projects/plans`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        project_id: projectId,
        plan_ids: planIds,
      }),
    }
  );
};

// GET - Buscar um plano de projeto com comissões (formato novo)
// Rota: /organizations/plans/:planId/with-commissions
// O backend busca o projectId internamente através do planId
export const getPlanWithCommissions = async (
  planId: string,
  parentOrganizationId: string
): Promise<{
  name: string;
  description?: string;
  products: Array<{
    product_id: string;
    name: string;
    type: string;
    capital_value: number;
    premium_value: number;
    description?: string;
    insurance_policy_extra_price_id?: string | null;
  }>;
  commission_distribution: {
    main_partner: Array<{
      organization_id: string;
      position: number;
      agency: number;
      commission: number;
    }>;
    partner_participant: Array<{
      organization_id: string;
      position: number;
      agency: number;
      commission: number;
    }>;
    partner_affiliate: Array<{
      level: number;
      position: number;
      agency: number;
      commission: number;
    }>;
  };
  commission_another: {
    main_partner: Array<{
      organization_id: string;
      markup: number;
      commission: number;
    }>;
    partner_participant: Array<{
      organization_id: string;
      markup: number;
      commission: number;
    }>;
    partner_affiliate: Array<{
      level: number;
      markup: number;
      commission: number;
    }>;
  };
  plan_price: number;
  plan_features: string[];
}> => {
  return customFetch(
    `/organizations/plans/${planId}/with-commissions`,
    {
      method: "GET",
      headers: {
        "x-organization-id": parentOrganizationId,
      },
    }
  );
};

// POST - Criar planos diretamente em um projeto (nova rota)
// Rota: /organizations/projects/:projectId/plans
// Usa o novo formato de commission_distribution com main_partner, partner_participant, partner_affiliate
export const createPlansInProject = async (
  projectId: string,
  data: Array<{
    name: string;
    description?: string;
    products: Array<{
      product_id: string;
      name: string;
      type: string;
      capital_value: number;
      premium_value: number;
      description?: string;
      insurance_policy_extra_price_id?: string | null;
    }>;
    commission_distribution: {
      main_partner: Array<{
        organization_id: string;
        position: number;
        agency: number;
        commission: number;
      }>;
      partner_participant: Array<{
        organization_id: string;
        position: number;
        agency: number;
        commission: number;
      }>;
      partner_affiliate: Array<{
        level: number;
        position: number;
        agency: number;
        commission: number;
      }>;
    };
    commission_another: {
      main_partner: Array<{
        organization_id: string;
        markup: number;
        commission: number;
      }>;
      partner_participant: Array<{
        organization_id: string;
        markup: number;
        commission: number;
      }>;
      partner_affiliate?: Array<{
        level: number;
        markup: number;
        commission: number;
      }>;
    };
    plan_price: number;
    plan_features: string[];
  }>
): Promise<any> => {
  return customFetch<any>(
    `/organizations/projects/${projectId}/plans`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );
};

// PATCH - Atualizar um plano em um projeto
// Rota: /organizations/projects/:projectId/plans/:id
// O organizationId vai no header x-organization-id (adicionado automaticamente pelo customFetch)
export const updatePlanInProject = async (
  _organizationId: string,
  projectId: string,
  planId: string,
  data: any
): Promise<any> => {
  return customFetch(
    `/organizations/projects/${projectId}/plans/${planId}`,
    {
      method: "PATCH",
      body: JSON.stringify(data),
    }
  );
};

// DELETE - Deletar um plano de um projeto
// Rota: /organizations/projects/:projectId/plans/:id
// O organizationId vai no header x-organization-id
export const deletePlanInProject = async (
  organizationId: string,
  projectId: string,
  planId: string
): Promise<{ success: boolean }> => {
  return customFetch(
    `/organizations/projects/${projectId}/plans/${planId}`,
    {
      method: "DELETE",
      headers: {
        "x-organization-id": organizationId,
      },
    }
  );
};

// GET - Buscar organização por CNPJ
// Rota: /organizations/cnpj/:cnpj
export const findOrganizationByCnpj = async (
  cnpj: string
): Promise<any> => {
  const cleanCnpj = cnpj.replace(/\D/g, "");
  return customFetch<any>(`/organizations/cnpj/${cleanCnpj}`, {
    method: "GET",
  });
};

// GET - Buscar organização PF por CPF
// Rota: /organizations/cpf/:cpf
export const findOrganizationByCpf = async (
  cpf: string
): Promise<any> => {
  const cleanCpf = cpf.replace(/\D/g, "");
  return customFetch<any>(`/organizations/cpf/${cleanCpf}`, {
    method: "GET",
  });
};

// POST - Criar organização para projeto de afiliado
// Rota: /organizations/:projectId/:fatherOrganizationId
export const createOrganizationForProject = async (
  projectId: string,
  fatherOrganizationId: string,
  data: FormData
): Promise<{ message: string; organization_id: string }> => {
  return customFetch<{ message: string; organization_id: string }>(
    `/organizations/${projectId}/${fatherOrganizationId}`,
    {
      method: "POST",
      body: data,
    }
  );
};

// POST - Associar organização existente ao projeto
// Rota: /projects/:projectId/organizations (tentativa de rota alternativa)
// Ou usar a mesma rota mas enviar os dados completos da organização encontrada
export const associateExistingOrganizationToProject = async (
  projectId: string,
  fatherOrganizationId: string,
  organizationId: string,
  level: string,
  organizationData?: any // Dados completos da organização encontrada
): Promise<{ message: string; organization_id: string }> => {
  console.log("associateExistingOrganizationToProject - Dados recebidos:", {
    projectId,
    fatherOrganizationId,
    organizationId,
    level,
    hasOrganizationData: !!organizationData,
  });

  // Tentar usar uma rota específica para associar organização existente
  // Se não existir, usar a mesma rota mas enviar os dados completos
  try {
    // Primeira tentativa: rota específica para associar organização existente
    try {
      const result = await customFetch<{ message: string; organization_id: string }>(
        `/projects/${projectId}/organizations/${organizationId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            level: parseInt(level),
            organization_father: fatherOrganizationId,
            commission_percentage: 0,
          }),
        }
      );
      
      console.log("associateExistingOrganizationToProject - Resposta da API (rota específica):", result);
      return result;
    } catch (error1: any) {
      // Se a rota específica não existir (404), usar a mesma rota mas com dados completos limpos
      if (error1.status === 404 && organizationData) {
        console.log("Rota específica não encontrada, tentando com dados completos limpos...");
        
        const formData = new FormData();
        
        // Limpar e formatar os dados da organização, removendo campos que não devem existir
        const cleanAddress = organizationData.address ? {
          postal_code: organizationData.address.postal_code?.replace(/\D/g, "") || "",
          street: organizationData.address.street || "",
          number: organizationData.address.number || "",
          complement: organizationData.address.complement || "",
          neighborhood: organizationData.address.neighborhood || "",
          city: organizationData.address.city || "",
          state: organizationData.address.state || "",
        } : {};

        const cleanRepresentative = organizationData.company_representative ? {
          first_name: organizationData.company_representative.first_name || "",
          last_name: organizationData.company_representative.last_name || "",
          personal_email: organizationData.company_representative.personal_email || "",
          cpf: organizationData.company_representative.cpf?.replace(/\D/g, "") || "",
          mobile_phone: organizationData.company_representative.mobile_phone?.replace(/\D/g, "") || "",
        } : {};

        const cleanBankAccount = organizationData.bank_account ? {
          bank_name: organizationData.bank_account.bank_name || "",
          bank_branch: organizationData.bank_account.bank_branch || "",
          checking_account: organizationData.bank_account.checking_account || "",
          checking_account_digit: organizationData.bank_account.checking_account_digit || "",
        } : {};

        // Preparar dados JSON no formato esperado pela API (sem campos extras)
        const jsonData = {
          name: organizationData.name || "",
          cnpj: organizationData.cnpj?.replace(/\D/g, "") || "",
          corporate_name: organizationData.corporate_name || "",
          email: organizationData.email || "",
          opening_date: organizationData.opening_date || new Date().toISOString().split('T')[0],
          business_classification: organizationData.business_classification || "",
          address: cleanAddress,
          company_representative: cleanRepresentative,
          bank_account: cleanBankAccount,
          signed_terms: {
            signed: true,
            signed_at: new Date().toISOString(),
            ip_address: "127.0.0.1",
          },
        };

        formData.append("data", JSON.stringify(jsonData));

        // Adicionar documentos se existirem e forem Files
        if (organizationData.documents) {
          if (organizationData.documents.company_logo instanceof File) {
            formData.append("document.company_logo", organizationData.documents.company_logo);
          }
          if (organizationData.documents.cnpj_card instanceof File) {
            formData.append("document.cnpj_card", organizationData.documents.cnpj_card);
          }
          if (organizationData.documents.partners_documents_cpf) {
            const cpfDocs = Array.isArray(organizationData.documents.partners_documents_cpf) 
              ? organizationData.documents.partners_documents_cpf 
              : [organizationData.documents.partners_documents_cpf];
            cpfDocs.forEach((file: File) => {
              if (file instanceof File) {
                formData.append("document.partners_documents_cpf", file);
              }
            });
          }
          if (organizationData.documents.articles_of_incorporation) {
            const articles = Array.isArray(organizationData.documents.articles_of_incorporation)
              ? organizationData.documents.articles_of_incorporation
              : [organizationData.documents.articles_of_incorporation];
            articles.forEach((file: File) => {
              if (file instanceof File) {
                formData.append("document.articles_of_incorporation", file);
              }
            });
          }
          if (organizationData.documents.signed_contract || organizationData.documents.contract_signed) {
            const contracts = Array.isArray(organizationData.documents.signed_contract || organizationData.documents.contract_signed)
              ? (organizationData.documents.signed_contract || organizationData.documents.contract_signed)
              : [organizationData.documents.signed_contract || organizationData.documents.contract_signed];
            contracts.forEach((file: File) => {
              if (file instanceof File) {
                formData.append("document.contract_signed", file);
              }
            });
          }
        }

        const result = await customFetch<{ message: string; organization_id: string }>(
          `/organizations/${projectId}/${fatherOrganizationId}`,
          {
            method: "POST",
            body: formData,
          }
        );
        
        console.log("associateExistingOrganizationToProject - Resposta da API (com dados completos):", result);
        return result;
      }
      throw error1;
    }
  } catch (error: any) {
    console.error("associateExistingOrganizationToProject - Erro na API:", error);
    throw error;
  }
};

// GET - Listar produtos de um projeto
// Rota: /projects/:id/products
// O organizationId vai no header x-organization-id (adicionado automaticamente pelo customFetch)
export const getProjectProducts = async (
  projectId: string
): Promise<Array<{ id: string; name: string | null }>> => {
  return customFetch<Array<{ id: string; name: string | null }>>(
    `/projects/${projectId}/products`,
    {
      method: "GET",
    }
  );
};

// POST - Adicionar produtos a um projeto
// Rota: /projects/:id/products
// O organizationId vai no header x-organization-id (adicionado automaticamente pelo customFetch)
export const addProductsToProject = async (
  projectId: string,
  productIds: string[]
): Promise<{ message: string }> => {
  return customFetch<{ message: string }>(
    `/projects/${projectId}/products`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        product_ids: productIds,
      }),
    }
  );
};

// PATCH - Atualizar lista de produtos de um projeto
// Rota: /projects/:id/products
// O organizationId vai no header x-organization-id (adicionado automaticamente pelo customFetch)
export const updateProjectProducts = async (
  projectId: string,
  productIds: string[]
): Promise<{ message: string }> => {
  return customFetch<{ message: string }>(
    `/projects/${projectId}/products`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        product_ids: productIds,
      }),
    }
  );
};
